// /plans/:planId — configuracion vigente + linea de tiempo de versiones.
//
// Por que una pagina propia y no un tab mas de PartnerEdit: un plan dejo de
// ser un formulario editable. Es una linea de tiempo de versiones inmutables,
// y el acto central ("publicar un precio nuevo") merece su propio flujo con
// diff y motivo obligatorio. PartnerEdit ya tiene 2700 lineas.

import { useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listPlanVersions,
  listPlanTags,
  publishPlanVersion,
  AdminApiError,
  type PlanVersion,
  type PublishPlanVersionBody,
} from '@/lib/api';
import PlanFeaturesEditor, {
  toRows,
  fromRows,
  describeFeatures,
  type FeatureRow,
} from '@/components/PlanFeaturesEditor';
import PlanTagsEditor from '@/components/PlanTagsEditor';
import { useSession } from '@/hooks/useSession';
import { fieldCls } from '@/lib/form-styles';
import { canWrite } from '@/lib/access';

const money = (amount: number, currency: string): string =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency, maximumFractionDigits: 2 }).format(
    amount,
  );

const dateTime = (iso: string): string =>
  new Intl.DateTimeFormat('es-AR', { dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(iso),
  );

const PERIOD_LABEL: Record<PlanVersion['billing_period'], string> = {
  monthly: 'mensual',
  yearly: 'anual',
  weekly: 'semanal',
};

/** Variacion porcentual entre dos versiones consecutivas. */
function pctChange(from: number, to: number): string | null {
  if (from === 0) return null;
  const pct = ((to - from) / from) * 100;
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(1)}%`;
}

// Limites: null/undefined = sin limite. El input vacio representa ese caso;
// 0 es distinto de vacio (0 = bloqueado, no ilimitado).
const limitToInput = (v: number | null | undefined): string =>
  v === null || v === undefined ? '' : String(v);

const inputToLimit = (s: string): number | null => (s.trim() === '' ? null : Number(s));

const limitLabel = (v: number | null | undefined): string =>
  v === null || v === undefined ? 'sin limite' : String(v);

export default function PlanDetail(): JSX.Element {
  const { planId = '' } = useParams();
  const session = useSession();
  const canPublish = canWrite(session, 'partners');
  const [publishing, setPublishing] = useState(false);

  const versionsQ = useQuery({
    queryKey: ['admin-plan-versions', planId],
    queryFn: () => listPlanVersions(planId),
  });

  // El partner_id sale de aca: la ruta solo trae planId, y el editor de tags
  // necesita el partner para levantar su catalogo.
  const planTagsQ = useQuery({
    queryKey: ['admin-plan-tags', planId],
    queryFn: () => listPlanTags(planId),
  });

  const current = versionsQ.data?.find((v) => v.is_current);
  const history = versionsQ.data ?? [];

  return (
    <div className="space-y-6">
      <div>
        <Link to="/partners" className="text-sm text-brand hover:underline">
          ← Partners
        </Link>
        <h1 data-help-page="plan.page" data-help="plan.page" className="mt-1 text-xl font-semibold text-ink dark:text-ink-inverted">{planId}</h1>
        {current ? (
          <p className="text-sm text-ink-muted">
            {current.display_name} · {current.code} · v{current.version} vigente
          </p>
        ) : null}
      </div>

      {versionsQ.isLoading ? <p className="text-sm text-ink-muted">Cargando…</p> : null}
      {versionsQ.isError ? (
        <p className="text-sm text-red-600">
          {versionsQ.error instanceof AdminApiError
            ? versionsQ.error.message
            : 'No se pudo cargar el historial.'}
        </p>
      ) : null}

      {current ? (
        <section
          data-help="plan.current"
          className="rounded-lg border border-surface-border dark:border-surface-dark-border p-4"
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-wide text-ink-muted">Precio vigente</p>
              <p className="mt-1 text-3xl font-semibold text-ink dark:text-ink-inverted">
                {money(current.price_amount, current.price_currency)}
              </p>
              <p className="text-sm text-ink-muted">
                {PERIOD_LABEL[current.billing_period]} · {current.max_concurrent_streams} streams ·{' '}
                {current.max_devices} devices
                {current.trial_days > 0 ? ` · ${current.trial_days}d de prueba` : ''}
              </p>
              {current.features && current.features.length > 0 ? (
                <ul className="mt-2 space-y-0.5" data-help="plan.features">
                  {current.features.map((f, i) => {
                    const included = typeof f === 'string' || f.included !== false;
                    return (
                      <li
                        key={i}
                        className={`text-sm ${included ? 'text-ink dark:text-ink-inverted' : 'text-ink-muted line-through'}`}
                      >
                        <span aria-hidden className="mr-1.5">
                          {included ? '✓' : '✕'}
                        </span>
                        {typeof f === 'string' ? f : f.label}
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="mt-2 text-sm text-ink-muted">Sin features configuradas.</p>
              )}
              <p className="mt-2 text-xs text-ink-muted">
                Vigente desde {dateTime(current.valid_from)}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setPublishing(true)}
              disabled={!canPublish}
              title={canPublish ? undefined : 'Requiere rol admin'}
              className="min-h-[2.75rem] shrink-0 rounded-lg bg-brand hover:bg-brand-hover px-4 text-sm font-medium text-white disabled:opacity-50"
            >
              {/* No dice "Cambiar precio": el diálogo edita TODA la config
                  versionada del plan (nombre, límites, features, prueba). */}
              Modificar plan
            </button>
          </div>
        </section>
      ) : null}

      {/* Config NO versionada del plan. Va entre la config vigente y el
          historial porque se guarda sola, sin publicar version. */}
      {planTagsQ.data ? (
        <div data-help="plan.tags">
          <PlanTagsEditor
            planId={planId}
            partnerId={planTagsQ.data.partner_id}
            canWrite={canPublish}
          />
        </div>
      ) : null}

      <section data-help="plan.versions">
        <h2 className="text-sm font-semibold text-ink dark:text-ink-inverted">
          Historial de precios
        </h2>
        <p className="mt-0.5 text-xs text-ink-muted">
          Registro inmutable. Cada version queda encadenada con un hash: si alguien la modificara,
          la verificacion de auditoria lo detecta.
        </p>

        <ol className="mt-3 space-y-2">
          {history.map((v, i) => {
            // history viene ordenado de la mas nueva a la mas vieja.
            const prev = history[i + 1];
            const delta = prev ? pctChange(prev.price_amount, v.price_amount) : null;
            return (
              <li
                key={v.plan_version_id}
                className="rounded-lg border border-surface-border dark:border-surface-dark-border p-3"
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <div className="flex items-baseline gap-2">
                    <span className="font-medium text-ink dark:text-ink-inverted">
                      v{v.version}
                    </span>
                    <span className="text-lg text-ink dark:text-ink-inverted">
                      {money(v.price_amount, v.price_currency)}
                    </span>
                    {delta ? (
                      <span
                        className={
                          v.price_amount > (prev?.price_amount ?? 0)
                            ? 'text-xs font-medium text-amber-700 dark:text-amber-500'
                            : 'text-xs font-medium text-emerald-700 dark:text-emerald-500'
                        }
                      >
                        {delta}
                      </span>
                    ) : null}
                    {v.is_current ? (
                      <span className="rounded bg-brand/10 px-1.5 py-0.5 text-xs font-medium text-brand">
                        vigente
                      </span>
                    ) : null}
                  </div>
                  <span className="text-xs text-ink-muted">
                    {dateTime(v.valid_from)}
                    {v.valid_until ? ` → ${dateTime(v.valid_until)}` : ''}
                  </span>
                </div>

                {v.change_reason ? (
                  <p className="mt-1 text-sm text-ink dark:text-ink-inverted">{v.change_reason}</p>
                ) : null}
                <p className="mt-1 text-xs text-ink-muted">
                  por {v.published_by} · {PERIOD_LABEL[v.billing_period]} ·{' '}
                  {v.max_concurrent_streams} streams · {v.max_devices} devices
                </p>
                <p className="mt-1 break-all font-mono text-[10px] text-ink-muted">
                  hash {v.row_hash.slice(0, 16)}…
                </p>
              </li>
            );
          })}
        </ol>
      </section>

      {current && publishing ? (
        <PublishVersionDialog
          planId={planId}
          current={current}
          onClose={() => setPublishing(false)}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dialogo de publicacion
// ---------------------------------------------------------------------------

function PublishVersionDialog({
  planId,
  current,
  onClose,
}: {
  planId: string;
  current: PlanVersion;
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  // Arranca con la config vigente: publicar es declarar el estado completo,
  // asi que el admin parte de lo que hay y cambia lo que quiere.
  const [priceAmount, setPriceAmount] = useState(String(current.price_amount));
  const [displayName, setDisplayName] = useState(current.display_name);
  const [streams, setStreams] = useState(String(current.max_concurrent_streams));
  const [devices, setDevices] = useState(String(current.max_devices));
  const [trialDays, setTrialDays] = useState(String(current.trial_days));
  // Limites: '' = sin limite (null). Se editan aca y no en PartnerEdit porque
  // son parte de la config VERSIONADA — la DB rechaza cambiarlos por fuera.
  const [maxFavs, setMaxFavs] = useState(limitToInput(current.limits?.max_favorites));
  const [maxFavsPerType, setMaxFavsPerType] = useState(
    limitToInput(current.limits?.max_favorites_per_type),
  );
  // Features: se editan aca porque son config VERSIONADA (la DB rechaza
  // cambiarlas por fuera, igual que el precio). Antes se reenviaban tal cual
  // porque no habia editor.
  const [featureRows, setFeatureRows] = useState<FeatureRow[]>(() => toRows(current.features));
  const [reason, setReason] = useState('');

  const parsedPrice = Number(priceAmount);
  const priceValid = Number.isFinite(parsedPrice) && parsedPrice >= 0;
  const reasonValid = reason.trim().length >= 8;

  const delta = useMemo(
    () => (priceValid ? pctChange(current.price_amount, parsedPrice) : null),
    [priceValid, parsedPrice, current.price_amount],
  );

  const publish = useMutation({
    mutationFn: (): Promise<unknown> => {
      const body: PublishPlanVersionBody = {
        display_name: displayName,
        price_amount: parsedPrice,
        price_currency: current.price_currency,
        billing_period: current.billing_period,
        max_concurrent_streams: Number(streams),
        max_devices: Number(devices),
        trial_days: Number(trialDays),
        // El body declara el estado COMPLETO de la version: lo que no se manda
        // se borra. Por eso este dialogo se construye sobre PlanVersion (que
        // trae el snapshot entero) y nunca sobre AdminPlan (que no trae
        // features).
        features: fromRows(featureRows),
        limits: {
          max_favorites: inputToLimit(maxFavs),
          max_favorites_per_type: inputToLimit(maxFavsPerType),
        },
        change_reason: reason.trim(),
        // Optimistic locking: si otro admin publico mientras este dialogo
        // estaba abierto, el backend responde 409 en vez de pisarlo.
        expected_current_version: current.version,
      };
      return publishPlanVersion(planId, body);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['admin-plan-versions', planId] });
      await qc.invalidateQueries({ queryKey: ['admin-plans'] });
      onClose();
    },
  });

  const changed = [
    ...(displayName !== current.display_name
      ? [{ label: 'Nombre', from: current.display_name, to: displayName }]
      : []),
    ...(priceValid && parsedPrice !== current.price_amount
      ? [
          {
            label: 'Precio',
            from: money(current.price_amount, current.price_currency),
            to: `${money(parsedPrice, current.price_currency)}${delta ? ` (${delta})` : ''}`,
          },
        ]
      : []),
    ...(Number(streams) !== current.max_concurrent_streams
      ? [{ label: 'Streams', from: String(current.max_concurrent_streams), to: streams }]
      : []),
    ...(Number(devices) !== current.max_devices
      ? [{ label: 'Devices', from: String(current.max_devices), to: devices }]
      : []),
    ...(Number(trialDays) !== current.trial_days
      ? [{ label: 'Prueba (dias)', from: String(current.trial_days), to: trialDays }]
      : []),
    ...(inputToLimit(maxFavs) !== (current.limits?.max_favorites ?? null)
      ? [
          {
            label: 'Max favoritos',
            from: limitLabel(current.limits?.max_favorites),
            to: limitLabel(inputToLimit(maxFavs)),
          },
        ]
      : []),
    ...(inputToLimit(maxFavsPerType) !== (current.limits?.max_favorites_per_type ?? null)
      ? [
          {
            label: 'Max favoritos por tipo',
            from: limitLabel(current.limits?.max_favorites_per_type),
            to: limitLabel(inputToLimit(maxFavsPerType)),
          },
        ]
      : []),
    // Comparar el JSON serializado y no las filas: `fromRows` descarta las
    // vacias y colapsa las incluidas a string, asi que dos borradores
    // distintos pueden producir la MISMA version — y eso no es un cambio.
    ...(JSON.stringify(fromRows(featureRows)) !== JSON.stringify(current.features ?? [])
      ? [
          {
            label: 'Features',
            from: describeFeatures(current.features ?? []),
            to: describeFeatures(fromRows(featureRows)),
          },
        ]
      : []),
  ];

  const conflict = publish.error instanceof AdminApiError && publish.error.code === 'version_conflict';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-full w-full max-w-lg overflow-y-auto rounded-xl bg-surface dark:bg-surface-dark p-5 shadow-xl">
        <h2 className="text-lg font-semibold text-ink dark:text-ink-inverted">
          Publicar version v{current.version + 1}
        </h2>
        <p className="mt-1 text-xs text-ink-muted">
          La version v{current.version} se cierra y queda en el historial. Los cobros que ya se
          hicieron con ella no se tocan.
        </p>

        <div className="mt-4 space-y-3">
          <Field label="Nombre">
            <input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className={fieldCls({ size: 'lg' })}
            />
          </Field>

          <Field label={`Precio (${current.price_currency})`}>
            <input
              value={priceAmount}
              onChange={(e) => setPriceAmount(e.target.value)}
              inputMode="decimal"
              className={fieldCls({ size: 'lg' })}
            />
            {!priceValid ? (
              <p className="mt-1 text-xs text-red-600">Tiene que ser un numero &gt;= 0.</p>
            ) : null}
          </Field>

          <div className="grid grid-cols-3 gap-2">
            <Field label="Streams">
              <input
                value={streams}
                onChange={(e) => setStreams(e.target.value)}
                inputMode="numeric"
                className={fieldCls({ size: 'lg' })}
              />
            </Field>
            <Field label="Devices">
              <input
                value={devices}
                onChange={(e) => setDevices(e.target.value)}
                inputMode="numeric"
                className={fieldCls({ size: 'lg' })}
              />
            </Field>
            <Field label="Prueba (d)">
              <input
                value={trialDays}
                onChange={(e) => setTrialDays(e.target.value)}
                inputMode="numeric"
                className={fieldCls({ size: 'lg' })}
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Field label="Max favoritos">
              <input
                value={maxFavs}
                onChange={(e) => setMaxFavs(e.target.value)}
                inputMode="numeric"
                placeholder="sin limite"
                className={fieldCls({ size: 'lg' })}
              />
            </Field>
            <Field label="Max favoritos por tipo">
              <input
                value={maxFavsPerType}
                onChange={(e) => setMaxFavsPerType(e.target.value)}
                inputMode="numeric"
                placeholder="sin limite"
                className={fieldCls({ size: 'lg' })}
              />
            </Field>
          </div>

          <div className="border-t border-surface-border pt-3 dark:border-surface-dark-border">
            <PlanFeaturesEditor rows={featureRows} onChange={setFeatureRows} />
          </div>

          <Field label="Motivo del cambio">
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ej: Ajuste por inflacion — IPC julio 8.2%"
              className={fieldCls({ size: 'lg' })}
            />
            <p className="mt-1 text-xs text-ink-muted">
              Queda en el registro de auditoria, junto a tu usuario y la fecha. Minimo 8 caracteres.
            </p>
          </Field>
        </div>

        {/* Diff antes de confirmar: que el admin vea exactamente que va a cambiar. */}
        <div className="mt-4 rounded-lg border border-surface-border dark:border-surface-dark-border p-3">
          <p className="text-xs font-medium uppercase tracking-wide text-ink-muted">Cambios</p>
          {changed.length === 0 ? (
            <p className="mt-1 text-sm text-ink-muted">Sin cambios respecto de la version vigente.</p>
          ) : (
            <ul className="mt-1 space-y-1">
              {changed.map((c) => (
                <li key={c.label} className="text-sm text-ink dark:text-ink-inverted">
                  <span className="text-ink-muted">{c.label}:</span>{' '}
                  <span className="line-through text-ink-muted">{c.from}</span>{' '}
                  <span aria-hidden>→</span> <span className="font-medium">{c.to}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {publish.isError ? (
          <p className="mt-3 text-sm text-red-600">
            {conflict
              ? 'Otro admin publico una version mientras tenias esto abierto. Cerra y volve a cargar para no pisar su cambio.'
              : publish.error instanceof AdminApiError
                ? publish.error.message
                : 'No se pudo publicar.'}
          </p>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="min-h-[2.75rem] rounded-lg border border-surface-border dark:border-surface-dark-border px-4 text-sm text-ink dark:text-ink-inverted"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => publish.mutate()}
            disabled={
              !priceValid || !reasonValid || changed.length === 0 || publish.isPending
            }
            className="min-h-[2.75rem] rounded-lg bg-brand hover:bg-brand-hover px-4 text-sm font-medium text-white disabled:opacity-50"
          >
            {publish.isPending ? 'Publicando…' : `Publicar v${current.version + 1}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-ink-muted">{label}</span>
      {children}
    </label>
  );
}
